<script>
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import LogTransactionModal from '../components/LogTransactionModal.svelte';
  import { authFetch } from '../authService'; // <-- IMPORT THIS

  let achievements = [];
  let isLoading = true;
  let error = '';
  let currentUser = null;

  // UI State
  let showHistory = false; // Default to hidden
  
  // Modal State
  let showLogModal = false;
  let selectedAchievementId = null;
  let selectedPromotionName = '';

  authStore.subscribe(value => {
    currentUser = value.user;
  });

  $: if (currentUser) {
    fetchAchievements();
  }

  // --- FILTERING & SORTING LOGIC ---
  // 1. Active: Not completed AND Not expired. Sorted by Name.
  $: activeAchievements = achievements.filter(a => {
      const isCompleted = !!a.actualCompletionDate;
      const isExpired = new Date(a.targetCompletionDate) < new Date();
      return !isCompleted && !isExpired;
  }).sort((a, b) => a.promotionName.localeCompare(b.promotionName));

  // 2. History: Completed OR Expired.
  $: historyAchievements = achievements.filter(a => {
      const isCompleted = !!a.actualCompletionDate;
      const isExpired = new Date(a.targetCompletionDate) < new Date();
      return isCompleted || isExpired;
  });

  async function fetchAchievements() {
    isLoading = true;
    error = '';
    try {
      const userId = currentUser?.idTokenClaims?.oid;
      if (!userId) {
         throw new Error('You must be logged in to view this page.');
      }
      const response = await authFetch(getApiUrl(`/achievements/user/${userId}`));
      if (!response.ok) throw new Error('Could not fetch your achievements.');
      achievements = await response.json();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function formatCompletionDate(dateString) {
    if (!dateString) return 'In Progress';
    return `Completed on ${new Date(dateString).toLocaleDateString()}`;
  }

  function openQuickLog(e, achievement) {
    e.preventDefault();
    selectedAchievementId = achievement._id;
    selectedPromotionName = achievement.promotionName;
    showLogModal = true;
  }

  function handleLogSuccess() {
    fetchAchievements();
    alert("Transaction logged successfully!");
  }
</script>

<LogTransactionModal 
  bind:showModal={showLogModal}
  achievementId={selectedAchievementId}
  promotionName={selectedPromotionName}
  on:success={handleLogSuccess}
  on:close={() => showLogModal = false}
/>

<div class="page-container">
  <h2>My Achievements</h2>

  {#if isLoading}
    <p>Loading your tracked achievements...</p>
  {:else if error}
    <p class="error">{error}</p>
  {:else if achievements.length === 0}
    <div class="empty-state">
      <p>You are not tracking any promotion achievements yet.</p>
      <Link to="/" class="browse-link">Browse Promotions</Link>
    </div>
  {:else}
    
    <!-- ACTIVE ACHIEVEMENTS SECTION -->
    <div class="section-header">
      <h3>In Progress ({activeAchievements.length})</h3>
    </div>

    {#if activeAchievements.length === 0}
      <p class="info-text">No active challenges. Check your history or join new ones!</p>
    {:else}
      <div class="results-list">
        {#each activeAchievements as achievement (achievement._id)}
          <div class="card-wrapper">
              <Link to={`/achievements/detail/${achievement._id}`} class="product-card-link">
                  <div class="product-card">
                      <div class="card-header">
                          <h4>{achievement.promotionName}</h4>
                      </div>
                      
                      <div class="card-body">
                          <div class="progress-row">
                              <span class="label">Transactions:</span>
                              <span class="value">
                                  {#if achievement.maximumTargetNumTransactions > 0}
                                      {achievement.actualNumTransactions} / {achievement.maximumTargetNumTransactions}
                                      {#if achievement.targetNumTransactions > 0}
                                          <span style="font-size: 0.8rem; font-weight: normal; color: #666;"> (Interim: {achievement.targetNumTransactions})</span>
                                      {/if}
                                  {:else if achievement.targetNumTransactions > 0}
                                      {achievement.actualNumTransactions} / {achievement.targetNumTransactions}
                                  {:else}
                                      {achievement.actualNumTransactions} / N/A
                                  {/if}
                              </span>
                          </div>
                          <div class="progress-row">
                              <span class="label">Spending:</span>
                              <span class="value">
                                  {#if achievement.maximumTargetAccumulatedSpending > 0}
                                      ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.maximumTargetAccumulatedSpending.toFixed(2)}
                                      {#if achievement.minimumTargetAccumulatedSpending > 0}
                                          <span style="font-size: 0.8rem; font-weight: normal; color: #666;"> (Interim: ${achievement.minimumTargetAccumulatedSpending.toFixed(2)})</span>
                                      {/if}
                                  {:else if achievement.minimumTargetAccumulatedSpending > 0}
                                      ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.minimumTargetAccumulatedSpending.toFixed(2)}
                                  {:else if achievement.targetAccumulatedSpending > 0}
                                      ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.targetAccumulatedSpending.toFixed(2)}
                                  {:else}
                                      ${achievement.actualAccumulatedSpending.toFixed(2)} / N/A
                                  {/if}
                              </span>
                          </div>
                          <p class="status in-progress">In Progress</p>
                      </div>
                      
                      <div class="card-footer">
                          <div class="date-info">
                              Ends: {new Date(achievement.targetCompletionDate).toLocaleDateString()}
                          </div>
                          <button class="quick-log-btn" on:click={(e) => openQuickLog(e, achievement)}>
                              ⚡ Quick Log
                          </button>
                      </div>
                  </div>
              </Link>
          </div>
        {/each}
      </div>
    {/if}

    <!-- HISTORY SECTION TOGGLE -->
    <div class="history-toggle-container">
      <button class="toggle-btn" on:click={() => showHistory = !showHistory}>
        {showHistory ? 'Hide' : 'Show'} Completed & Expired ({historyAchievements.length}) {showHistory ? '▲' : '▼'}
      </button>
    </div>

    <!-- HISTORY LIST -->
    {#if showHistory}
      <div class="results-list history-list">
        {#each historyAchievements as achievement (achievement._id)}
          <div class="card-wrapper">
              <Link to={`/achievements/detail/${achievement._id}`} class="product-card-link">
                  <div class="product-card grayscale"> <!-- Added grayscale class for visual distinction -->
                      <div class="card-header">
                          <h4>{achievement.promotionName}</h4>
                      </div>
                      
                      <div class="card-body">
                          <div class="progress-row">
                              <span class="label">Transactions:</span>
                              <span class="value">
                                  {#if achievement.maximumTargetNumTransactions > 0}
                                      {achievement.actualNumTransactions} / {achievement.maximumTargetNumTransactions}
                                      {#if achievement.targetNumTransactions > 0}
                                          <span style="font-size: 0.8rem; font-weight: normal; color: #666;"> (Interim: {achievement.targetNumTransactions})</span>
                                      {/if}
                                  {:else if achievement.targetNumTransactions > 0}
                                      {achievement.actualNumTransactions} / {achievement.targetNumTransactions}
                                  {:else}
                                      {achievement.actualNumTransactions} / N/A
                                  {/if}
                              </span>
                          </div>
                          <div class="progress-row">
                              <span class="label">Spending:</span>
                              <span class="value">
                                  {#if achievement.maximumTargetAccumulatedSpending > 0}
                                      ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.maximumTargetAccumulatedSpending.toFixed(2)}
                                      {#if achievement.minimumTargetAccumulatedSpending > 0}
                                          <span style="font-size: 0.8rem; font-weight: normal; color: #666;"> (Interim: ${achievement.minimumTargetAccumulatedSpending.toFixed(2)})</span>
                                      {/if}
                                  {:else if achievement.minimumTargetAccumulatedSpending > 0}
                                      ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.minimumTargetAccumulatedSpending.toFixed(2)}
                                  {:else if achievement.targetAccumulatedSpending > 0}
                                      ${achievement.actualAccumulatedSpending.toFixed(2)} / ${achievement.targetAccumulatedSpending.toFixed(2)}
                                  {:else}
                                      ${achievement.actualAccumulatedSpending.toFixed(2)} / N/A
                                  {/if}
                              </span>
                          </div>
                          
                          <!-- Status Display -->
                          {#if achievement.actualCompletionDate}
                             <p class="status completed">🎉 {formatCompletionDate(achievement.actualCompletionDate)}</p>
                          {:else}
                             <p class="status expired">Expired</p>
                          {/if}
                      </div>
                      
                      <div class="card-footer">
                          <div class="date-info">
                              Ends: {new Date(achievement.targetCompletionDate).toLocaleDateString()}
                          </div>
                          <!-- No Quick Log for history items -->
                          <span class="view-link">View Details</span>
                      </div>
                  </div>
              </Link>
          </div>
        {/each}
      </div>
    {/if}

  {/if}
</div>

<style>
  .page-container { 
    max-width: 1200px; 
    margin: 2rem auto; 
    padding: 0 1rem; 
  }
  
  h3 { border-bottom: 2px solid #eee; padding-bottom: 0.5rem; color: #333; }
  .info-text { color: #666; font-style: italic; }

  /* Grid Layout */
  .results-list { 
      display: grid; 
      grid-template-columns: 1fr; 
      gap: 1rem; 
      margin-bottom: 2rem;
  }
  @media (min-width: 600px) {
      .results-list { grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); }
  }

  .product-card-link { text-decoration: none; color: inherit; display: block; height: 100%; }

  /* Card Styles */
  .product-card { 
      background: #fff; padding: 1rem; border-radius: 8px; 
      box-shadow: 0 2px 4px rgba(0,0,0,0.05); border: 1px solid #eee;
      display: flex; flex-direction: column; height: 100%; box-sizing: border-box;
      transition: transform 0.2s;
      min-width: 0; word-wrap: break-word;
  }
  .product-card:hover { transform: translateY(-3px); box-shadow: 0 5px 15px rgba(0,0,0,0.1); }
  
  /* Grayscale style for history items */
  .product-card.grayscale {
      background-color: #f9f9f9;
      opacity: 0.8;
  }
  .product-card.grayscale:hover {
      opacity: 1;
      transform: none;
  }

  .card-header h4 { margin: 0 0 0.5rem 0; font-size: 1.1rem; color: #0056b3; line-height: 1.3; }
  .card-body { flex-grow: 1; }

  .progress-row { display: flex; justify-content: space-between; margin-bottom: 0.5rem; font-size: 0.95rem; }
  .label { color: #666; font-weight: 500; }
  .value { font-weight: bold; }

  .status { font-weight: bold; font-size: 0.9rem; margin: 1rem 0 0 0; }
  .status.in-progress { color: #007bff; }
  .status.completed { color: #28a745; }
  .status.expired { color: #dc3545; }

  .card-footer { 
      display: flex; justify-content: space-between; align-items: center; 
      margin-top: 1rem; padding-top: 0.75rem; border-top: 1px solid #f1f1f1; 
  }
  
  .date-info { font-size: 0.85rem; color: #6c757d; }
  
  .quick-log-btn {
      background-color: #e2f0d9; color: #28a745; border: 1px solid #28a745;
      padding: 0.4rem 0.8rem; border-radius: 20px; font-weight: bold; font-size: 0.9rem;
      cursor: pointer; transition: all 0.2s; z-index: 10;
  }
  .quick-log-btn:hover { background-color: #28a745; color: white; }

  .view-link { font-size: 0.9rem; color: #007bff; text-decoration: underline; }
  
  /* Toggle Section */
  .history-toggle-container {
      text-align: center;
      margin: 2rem 0 1rem;
      border-top: 1px solid #eee;
      padding-top: 2rem;
  }
  .toggle-btn {
      background: none; border: 1px solid #ccc; padding: 0.5rem 1.5rem;
      border-radius: 20px; cursor: pointer; color: #555; font-size: 1rem;
      transition: background 0.2s;
  }
  .toggle-btn:hover { background-color: #f1f1f1; }

  .empty-state { text-align: center; padding: 3rem; background: #f8f9fa; border-radius: 8px; }
  .browse-link { display: inline-block; margin-top: 1rem; color: #007bff; font-weight: bold; }
  
  .error { color: red; text-align: center; }
</style>